<template>
  <div class="register-clinic-page">
    <page-cover :cover="cover" v-if="cover" />
    <div class="auth-page container py-5">
    <div class="row justify-content-center">
      <div class="col-md-7 col-lg-6">
        <div class="card auth-card">
          <h2 v-tx="'RegisterClinicTitle'">{{ tx('RegisterClinicTitle') }}</h2>
          <p class="muted" v-tx="'RegisterClinicSubtitle'">{{ tx('RegisterClinicSubtitle') }}</p>

          <div v-if="loading" class="status" v-tx="'RegisterClinicLoading'">{{ tx('RegisterClinicLoading') }}</div>

          <form v-else @submit.prevent="onSubmit">
            <label class="field">
              <span v-tx="'RegisterClinicEmail'">{{ tx('RegisterClinicEmail') }} *</span>
              <input type="email" v-model="email" required />
            </label>
            <label class="field">
              <span v-tx="'RegisterClinicPassword'">{{ tx('RegisterClinicPassword') }} *</span>
              <input type="password" v-model="password" required minlength="6" />
            </label>

            <DynamicField
              v-for="f in fields"
              :key="f.id"
              :field="f"
              :value="customValues[f.key] || ''"
              @input="onFieldInput(f.key, $event)"
            />

            <button type="submit" class="primary" :disabled="busy" v-tx="'RegisterClinicSubmit'">
              {{ busy ? tx('RegisterClinicSubmitting') : tx('RegisterClinicSubmit') }}
            </button>
          </form>

          <div class="alt">
            <router-link :to="{ name: 'login' }" v-tx="'RegisterClinicHasAccount'">{{ tx('RegisterClinicHasAccount') }}</router-link>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import DynamicField from "@/components/shared/DynamicField.vue";
import PageCover from "@/components/shared/PageCover.vue";

const FALLBACK = {
  az: {
    RegisterClinicTitle: "Klinika qeydiyyatı",
    RegisterClinicSubtitle: "Tibb bacılarının CV bazasına klinika kimi giriş üçün hesab yaradın.",
    RegisterClinicLoading: "Yüklənir…",
    RegisterClinicEmail: "Email",
    RegisterClinicPassword: "Şifrə",
    RegisterClinicSubmit: "Qeydiyyatdan keç",
    RegisterClinicSubmitting: "Göndərilir…",
    RegisterClinicSuccess: "Qeydiyyat uğurlu!",
    RegisterClinicFailed: "Qeydiyyat alınmadı",
    RegisterClinicHasAccount: "Hesabınız var? Daxil olun",
  },
  de: {
    RegisterClinicTitle: "Klinik-Registrierung",
    RegisterClinicSubtitle: "Erstellen Sie ein Konto, um als Klinik auf die CV-Datenbank zuzugreifen.",
    RegisterClinicLoading: "Wird geladen…",
    RegisterClinicEmail: "E-Mail",
    RegisterClinicPassword: "Passwort",
    RegisterClinicSubmit: "Registrieren",
    RegisterClinicSubmitting: "Wird gesendet…",
    RegisterClinicSuccess: "Registrierung erfolgreich!",
    RegisterClinicFailed: "Registrierung fehlgeschlagen",
    RegisterClinicHasAccount: "Sie haben ein Konto? Anmelden",
  },
  en: {
    RegisterClinicTitle: "Clinic registration",
    RegisterClinicSubtitle: "Create an account to access the CV database as a clinic.",
    RegisterClinicLoading: "Loading…",
    RegisterClinicEmail: "Email",
    RegisterClinicPassword: "Password",
    RegisterClinicSubmit: "Register",
    RegisterClinicSubmitting: "Submitting…",
    RegisterClinicSuccess: "Registration successful!",
    RegisterClinicFailed: "Registration failed",
    RegisterClinicHasAccount: "Have an account? Log in",
  },
  ru: {
    RegisterClinicTitle: "Регистрация клиники",
    RegisterClinicSubtitle: "Создайте аккаунт для доступа к базе резюме как клиника.",
    RegisterClinicLoading: "Загрузка…",
    RegisterClinicEmail: "Email",
    RegisterClinicPassword: "Пароль",
    RegisterClinicSubmit: "Зарегистрироваться",
    RegisterClinicSubmitting: "Отправка…",
    RegisterClinicSuccess: "Регистрация успешна!",
    RegisterClinicFailed: "Не удалось зарегистрироваться",
    RegisterClinicHasAccount: "Есть аккаунт? Войти",
  },
  tr: {
    RegisterClinicTitle: "Klinik kaydı",
    RegisterClinicSubtitle: "CV veritabanına klinik olarak erişmek için hesap oluşturun.",
    RegisterClinicLoading: "Yükleniyor…",
    RegisterClinicEmail: "E-posta",
    RegisterClinicPassword: "Şifre",
    RegisterClinicSubmit: "Kayıt ol",
    RegisterClinicSubmitting: "Gönderiliyor…",
    RegisterClinicSuccess: "Kayıt başarılı!",
    RegisterClinicFailed: "Kayıt başarısız",
    RegisterClinicHasAccount: "Hesabınız var mı? Giriş yapın",
  },
};

export default {
  name: "RegisterClinicPage",
  components: { DynamicField, PageCover },
  data() {
    return {
      email: "",
      password: "",
      fields: [],
      customValues: {},
      loading: true,
      busy: false,
    };
  },
  computed: {
    // Banner cover-i digər səhifələrdəki kimi header-menyuya bağlı
    // səhifədən (aboutTypeId) gəlir; admin "Bağlı səhifə"-ni dəyişsə də işləyir.
    cover() {
      const path = this.$route.path;
      const menus = (this.$store.getters["headerMenus/allMenus"] || []).filter((m) => m.path === path);
      const covers = (this.$store.state.pageCover && this.$store.state.pageCover.covers) || [];
      const key = "description_" + (this.$store.state.lang || "az");
      const withCover = menus
        .map((m) => ({ m, c: covers.find((c) => c.typeId === m.aboutTypeId) }))
        .filter((x) => x.c);
      const best = withCover.find((x) => x.m[key] && String(x.m[key]).trim()) || withCover[0];
      return best ? best.c : null;
    },
  },
  mounted() { this.loadFields(); },
  methods: {
    async loadFields() {
      this.loading = true;
      try {
        const res = await axios.get(`/clinic-fields`);
        const list = (res.data?.data || [])
          .filter((f) => f.isActive && f.fieldType !== 9) // exclude File from registration
          .sort((a, b) => a.orderIndex - b.orderIndex);
        this.fields = list;
        const init = {};
        list.forEach((f) => { init[f.key] = ""; });
        this.customValues = init;
      } catch (e) {
        this.fields = [];
      } finally {
        this.loading = false;
      }
    },
    onFieldInput(key, val) {
      this.$set(this.customValues, key, val);
    },
    async onSubmit() {
      this.busy = true;
      try {
        const payload = {
          email: this.email,
          password: this.password,
          customValues: this.customValues,
        };
        const role = await this.$store.dispatch("auth/registerClinic", payload);
        if (!role) {
          this.$toast.error(this.tx('RegisterClinicFailed'));
          return;
        }
        this.$toast.success(this.tx('RegisterClinicSuccess'));
        this.$router.push({ name: "candidates" });
      } catch (e) {
        // toast shown by interceptor
      } finally {
        this.busy = false;
      }
    },
    tx(key) {
      const v = this.$store.state.statics?.[key];
      if (v && String(v).trim()) return v;
      const lang = this.$store.state.lang || "az";
      return (FALLBACK[lang] || FALLBACK.az)[key] || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-page { min-height: 60vh; }
.auth-card {
  background: #fff; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 14px rgba(0,0,0,0.06);
  h2 { color: #1e3c72; margin-bottom: 0.25rem; }
  .muted { color: #64748b; margin-bottom: 1.5rem; font-size: 0.9rem; }
  .status { padding: 1.25rem; background: #f8fafc; border-radius: 8px; text-align: center; color: #666; }
  .field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.85rem;
    span { font-size: 0.78rem; color: #475569; font-weight: 600; }
    input, textarea { padding: 0.6rem 0.8rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; outline: none; resize: vertical;
      &:focus { border-color: #2a5298; }
    }
  }
  button.primary { width: 100%; padding: 0.7rem; background: #2a5298; color: #fff; border: 0; border-radius: 6px; cursor: pointer; font: inherit; font-weight: 600; margin-top: 0.5rem;
    &:hover:not(:disabled) { background: #1e3c72; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
  .alt { margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid #eef0f4; text-align: center; font-size: 0.85rem;
    a { color: #2a5298; text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
